/**
 * ประลองยุทธ (the arena): open to everyone, fought offline and automatically. Each fighter's build
 * — martial art, neigong, gear — is set beforehand; challenge one of five drawn from the ranking and
 * both fighters are brought into the ring and fight on their own until one falls. Beat someone ranked above you to take their place. When the day ends
 * the rank pays its reward and the ranking starts over.
 */
import type { EquipSlot } from './items';
import type { PassReward } from './pass';

/** A fighter's build: what they fight with. */
export interface ArenaBuild {
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  readonly art: string;
  /** Level of the art's moves (1–10). */
  readonly skillLevel: number;
  readonly neigong: string;
  readonly neigongLevel: number;
  readonly equipment: Partial<Record<EquipSlot, string>>;
}

export const ARENA = {
  /** Contenders on the ranking besides the player (the player starts just below them). */
  size: 50,
  /** Challenges a day. */
  attemptsPerDay: 10,
  /** How many are offered to choose from. */
  choices: 5,
  /** The bout is to the fall; this is only a safety net, after which more blood left wins. */
  fightSeconds: 180,
} as const;

/** Rewards when the day ends, by final rank (the first band whose `upTo` covers the rank). */
export const ARENA_REWARDS: readonly {
  readonly upTo: number;
  readonly label: string;
  readonly reward: PassReward;
}[] = [
  { upTo: 1, label: 'อันดับ 1', reward: { jade: 300, gold: 20000, title: 'arena-champion' } },
  { upTo: 3, label: 'อันดับ 2–3', reward: { jade: 200, gold: 12000 } },
  { upTo: 10, label: 'อันดับ 4–10', reward: { jade: 120, gold: 8000 } },
  { upTo: 25, label: 'อันดับ 11–25', reward: { jade: 60, gold: 5000 } },
  { upTo: 50, label: 'อันดับ 26–50', reward: { jade: 30, gold: 3000 } },
  { upTo: Infinity, label: 'นอกอันดับ', reward: { gold: 1000 } },
];

export const arenaReward = (rank: number) => ARENA_REWARDS.find((r) => rank <= r.upTo)!;

const NAMES = [
  'กระบี่ไร้เงา',
  'จ้าวยุทธภพ',
  'หมัดเทวะ',
  'ดาบพิฆาตมาร',
  'นางฟ้าหิมะ',
  'เซียนสุรา',
  'มังกรซ่อนกาย',
  'พยัคฆ์ขาว',
  'ลมพายุทะเลทราย',
  'ราชาเพลงกระบี่',
  'จันทร์เสี้ยวพิฆาต',
  'หงส์เพลิง',
  'อินทรีทองคำ',
  'เทพธิดาบุปผา',
  'นักพรตเมฆา',
  'หลวงจีนเหล็ก',
  'บัณฑิตพู่กันเหล็ก',
  'มือสังหารเงาจันทร์',
  'ศิษย์ใหญ่ยอดหมอก',
  'องครักษ์ดาบทอง',
  'สาวน้อยพิษ',
  'ดาบเดียวพเนจร',
  'จอมยุทธ์สุราเมา',
  'หมาป่าเดียวดาย',
  'ฟ้าคำราม',
  'เมฆาลอยล่อง',
  'หิมะพันปี',
  'หยกดำ',
  'เพลิงโลหิต',
  'สายลมวสันต์',
  'เกล็ดมังกร',
  'นกกระเรียนขาว',
  'ภูผาเหล็ก',
  'สายน้ำไหล',
  'ใบไผ่ร่วง',
  'แสงอรุณ',
  'ราตรีมืด',
  'คลื่นทะเล',
  'ดอกท้อ',
  'ไผ่เขียว',
  'หินผา',
  'ฝนพรำ',
  'ศิษย์ใหม่หลี่',
  'ศิษย์ใหม่จาง',
  'คนตัดฟืน',
  'เด็กเลี้ยงวัว',
  'พ่อค้าถือดาบ',
  'ยามหมู่บ้าน',
  'นักเดินทาง',
  'ชาวบ้านฝึกหมัด',
];
const TITLES = ['ยอดฝีมือยุทธจักร', 'จอมยุทธ์พเนจร', 'ศิษย์สำนักใหญ่', 'ผู้ท้าประลอง', 'มือใหม่ยุทธภพ'];
const SPRITES = [
  'heroWudang',
  'red',
  'night',
  'lilac',
  'taoist',
  'monk',
  'guardOfficer',
  'blissLady',
  'scholar',
  'bloodMaster',
];
const ARTS = [
  'breeze-sword',
  'drift-fist',
  'drift-sword',
  'snow-sword',
  'bell-fist',
  'cloud-palm',
  'ink-slate',
  'nine-swords',
];
const GEAR: readonly Partial<Record<EquipSlot, string>>[] = [
  { weapon: 'crimson-sword', head: 'jade-crown', body: 'crimson-coat' },
  { weapon: 'azure-sword', head: 'bamboo-hat', body: 'wolf-pelt' },
  { weapon: 'azure-sword', head: 'red-band', body: 'black-vest' },
  { weapon: 'iron-sword', head: 'red-band', body: 'navy-coat' },
  { weapon: 'iron-sword', body: 'navy-coat' },
];

/** The contenders, strongest first: their starting rank is their index + 1. */
export const ARENA_ROSTER: readonly ArenaBuild[] = NAMES.slice(0, ARENA.size).map((name, i) => {
  const f = 1 - i / (ARENA.size - 1);
  return {
    name,
    title: TITLES[Math.min(TITLES.length - 1, Math.floor(i / 10))]!,
    sprite: SPRITES[i % SPRITES.length]!,
    art: ARTS[(i * 5) % ARTS.length]!,
    skillLevel: Math.max(1, Math.round(1 + 9 * f)),
    neigong: i % 2 ? 'cloud-heart' : 'blood-oath',
    neigongLevel: Math.max(1, Math.round(1 + 19 * f)),
    equipment: GEAR[Math.min(GEAR.length - 1, Math.floor(i / 10))]!,
  };
});

/** Han glyph on the arena's menu button. */
export const ARENA_GLYPHS = '擂';
